import { useMemo } from "react";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";
import { LineChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DatasetComponent,
  MarkLineComponent,
} from "echarts/components";
import { SVGRenderer } from "echarts/renderers";

type SitePhaseCurrentLiveUsageChartProps = {
  sitePhaseCurrentData: Array<{
    name: string;
    values: Array<{ timestamp: string; usage: number }>;
    style: { color: string };
  }> | undefined;
  maxCapacity: number;
  isSmallToMediumScreen: boolean;
  notMerge?: boolean;
};

export default function SitePhaseCurrentLiveUsageChart({
  sitePhaseCurrentData,
  maxCapacity,
  isSmallToMediumScreen,
  notMerge = false,
}: SitePhaseCurrentLiveUsageChartProps) {
  const options = useMemo(() => ({
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "line" },
      valueFormatter: (value: number) => (value != null ? `${Number(value).toFixed(2)} A` : "0 A"),
    },
    legend: {
      bottom: isSmallToMediumScreen ? 0 : 10,
      left: "center",
      itemGap: 20,
    },
    grid: {
      top: "5%",
      left: isSmallToMediumScreen ? "7%" : "3%",
      right: isSmallToMediumScreen ? "6%" : "3%",
      bottom: isSmallToMediumScreen ? "17%" : "13%",
      containLabel: true,
    },
    xAxis: {
      type: "time",
      name: "Time (last 12 hours)",
      nameLocation: "center",
      nameGap: 30,
      nameTextStyle: { fontWeight: "bold" },
      boundaryGap: false,
      axisLabel: { hideOverlap: true },
    },
    yAxis: {
      type: "value",
      name: "Current (A)",
      nameLocation: "middle",
      nameGap: isSmallToMediumScreen ? 38 : 55,
      nameTextStyle: { fontWeight: "bold" },
      max: maxCapacity,
      axisLabel: { formatter: (value: number) => `${value} A` },
    },
    animationDuration: 500,
    series: (sitePhaseCurrentData ?? []).map((series, index) => ({
      name: series.name,
      type: "line",
      emphasis: { focus: "series" },
      showSymbol: false,
      itemStyle: { color: series.style?.color },
      markLine:
        index === 0
          ? {
            data: [{
              name: "Max capacity",
              yAxis: maxCapacity,
              lineStyle: { color: "black" },
              label: { formatter: "Max capacity", distance: 10, position: "middle" },
            }],
            symbol: "circle",
          }
          : undefined,
      data: (series.values ?? []).map((v) => [v.timestamp, v.usage]),
    })),
  }), [isSmallToMediumScreen, sitePhaseCurrentData, maxCapacity]);

  echarts.use([TitleComponent, TooltipComponent, LegendComponent, GridComponent, DatasetComponent, LineChart, MarkLineComponent, SVGRenderer]);

  return (
    <div id="SitePhaseCurrentLiveUsageChart">
      <ReactEChartsCore
        echarts={echarts}
        option={options}
        style={{ width: "100%", height: isSmallToMediumScreen ? "350px" : "400px" }}
        opts={{ renderer: "svg" }}
        notMerge={notMerge}
      />
    </div>
  );
}
